/**
 * Article Index - Genera índice de H2, scroll spy y enlaces de ancla
 */
class ArticleIndex {
  constructor() {
    this.lists = document.querySelectorAll('.article-index__list[data-article-content]');
    this.drawer = document.getElementById('ArticleSidebarDrawer');
    this.headings = [];
    this.observer = null;
    this.init();
  }

  slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  ensureUniqueId(baseId, existingIds) {
    let id = baseId;
    let counter = 1;
    while (existingIds.has(id)) {
      id = `${baseId}-${counter}`;
      counter++;
    }
    existingIds.add(id);
    return id;
  }

  extractHeadings(contentEl) {
    const headings = [];
    const h2Elements = contentEl.querySelectorAll('h2');
    // Todos los id que ya hay en el contenido cuentan como ocupados; si un h2 ya trae el
    // suyo se usa ese (el enlace del índice apunta a un id que EXISTE) y solo se genera
    // uno para los que no lo tienen.
    const existingIds = new Set(Array.from(contentEl.querySelectorAll('[id]'), (el) => el.id));

    h2Elements.forEach((h2) => {
      const text = h2.textContent.trim();
      if (!text) return;

      let id = h2.id;
      if (!id) {
        id = this.ensureUniqueId(this.slugify(text) || 'heading', existingIds);
        h2.id = id;
      }

      headings.push({ id, text, element: h2 });
    });

    return headings;
  }

  buildIndexList(listEl) {
    listEl.innerHTML = '';
    this.headings.forEach(({ id, text }) => {
      const li = document.createElement('li');
      li.className = 'article-index__item';
      const a = document.createElement('a');
      a.href = `#${id}`;
      a.textContent = text;
      li.appendChild(a);
      listEl.appendChild(li);
    });
  }

  setActiveIndex(index) {
    this.lists.forEach((listEl) => {
      const items = listEl.querySelectorAll('.article-index__item');
      items.forEach((item, i) => {
        item.classList.toggle('article-index__item--active', i === index);
      });
    });
  }

  setupScrollSpy() {
    if (this.headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = this.headings.findIndex((h) => h.element === entry.target);
          if (idx >= 0) this.setActiveIndex(idx);
        });
      },
      {
        rootMargin: '-20% 0px -60% 0px',
        threshold: 0
      }
    );

    this.headings.forEach(({ element }) => observer.observe(element));
  }

  setupAnchors(listEl) {
    listEl.querySelectorAll('.article-index__item a').forEach((a, index) => {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const target = this.headings[index]?.element;
        // primero se cierra el drawer: abierto bloquea el scroll del fondo y el
        // desplazamiento no se haría
        if (this.drawer?.classList.contains('is-open')) this.closeDrawer(null, false);
        if (target) this.scrollToHeading(target);
      });
    });
  }

  /* La cabecera es sticky y se OCULTA al bajar pero REAPARECE al subir. Si solo se usa el
     scroll-margin del CSS, depende de que el CSS y la variable --header-height lleguen bien;
     aquí se mide la cabecera en el momento del clic y se deja siempre el mismo hueco
     (su altura + 20px), subiendo o bajando, así el título nunca queda tapado. */
  getScrollOffset() {
    const header = document.querySelector('.section-header');
    return (header ? header.offsetHeight : 0) + 20;
  }

  scrollToHeading(target) {
    const top = target.getBoundingClientRect().top + window.pageYOffset - this.getScrollOffset();
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  /* Botón del índice en móvil: es una pill en su sitio; al pasar de largo (sale por arriba de
     la pantalla) se reduce a un botón flotante abajo a la izquierda, y al volver a su posición
     vuelve a ser la pill. Se observa el HUECO (.article-sidebar__trigger-wrapper), que mide
     siempre lo mismo aunque el botón pase a position: fixed, así no hay bucle. Si el hueco
     está DEBAJO de la pantalla (aún no se ha llegado a él) no flota. */
  setupTriggerFloat() {
    const slot = document.querySelector('.article-sidebar__trigger-wrapper');
    if (!slot || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const top = entry.rootBounds ? entry.rootBounds.top : 0;
        slot.classList.toggle('is-floating', !entry.isIntersecting && entry.boundingClientRect.top < top);
      },
      // 60px: lo que tapa la cabecera sticky cuando reaparece al subir
      { rootMargin: '-60px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(slot);
  }

  setupDrawer() {
    const trigger = document.querySelector('[data-drawer-trigger]');
    const closeButtons = document.querySelectorAll('[data-drawer-close]');

    if (trigger && this.drawer) {
      trigger.addEventListener('click', () => this.openDrawer(trigger));
    }

    // Esc cierra el drawer, y Tab se queda dentro mientras está abierto
    document.addEventListener('keydown', (e) => {
      if (!this.drawer?.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        this.closeDrawer(trigger);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = this.drawer.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    closeButtons?.forEach((btn) => {
      btn.addEventListener('click', () => this.closeDrawer(trigger));
    });

    this.drawer?.querySelector('.article-sidebar-drawer__overlay')?.addEventListener('click', () => {
      this.closeDrawer(trigger);
    });

    this.setupSwipeToClose();
  }

  openDrawer(trigger) {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    // el fondo no se desplaza mientras el drawer está abierto
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.drawer.querySelector('.article-sidebar-drawer__close')?.focus();
  }

  closeDrawer(trigger, returnFocus = true) {
    this.drawer?.classList.remove('is-open');
    const t = trigger || document.querySelector('[data-drawer-trigger]');
    if (t) t.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = this.previousOverflow || '';
    if (returnFocus && t) t.focus();
  }

  /* Swipe a la izquierda para cerrar: el panel sigue al dedo. Solo cuenta si el gesto es
     mayormente horizontal (si no, es scroll vertical del índice y no se toca). Al soltar
     cierra si se arrastró más de 80px o fue un gesto rápido; si no, vuelve a su sitio. */
  setupSwipeToClose() {
    const panel = this.drawer?.querySelector('.article-sidebar-drawer__panel');
    const overlay = this.drawer?.querySelector('.article-sidebar-drawer__overlay');
    if (!panel) return;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let dx = 0;
    let axis = null; // null: sin decidir · 'x': arrastrando el panel · 'y': scroll vertical

    const reset = () => {
      panel.style.transition = '';
      panel.style.transform = '';
      if (overlay) {
        overlay.style.transition = '';
        overlay.style.opacity = '';
      }
    };

    panel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = Date.now();
      dx = 0;
      axis = null;
    }, { passive: true });

    panel.addEventListener('touchmove', (e) => {
      if (axis === 'y') return;
      const moveX = e.touches[0].clientX - startX;
      const moveY = e.touches[0].clientY - startY;
      if (axis === null) {
        if (Math.abs(moveX) < 8 && Math.abs(moveY) < 8) return; // aún no se sabe
        axis = Math.abs(moveX) > Math.abs(moveY) && moveX < 0 ? 'x' : 'y';
        if (axis === 'x') {
          panel.style.transition = 'none';
          if (overlay) overlay.style.transition = 'none';
        }
      }
      if (axis !== 'x') return;
      dx = Math.min(0, moveX); // solo hacia la izquierda
      panel.style.transform = `translateX(${dx}px)`;
      if (overlay) overlay.style.opacity = String(Math.max(0, 1 + dx / panel.offsetWidth));
    }, { passive: true });

    const end = () => {
      if (axis !== 'x') return;
      const fast = Date.now() - startTime < 300 && -dx > 30;
      const shouldClose = -dx > 80 || fast;
      reset();
      if (shouldClose) this.closeDrawer();
    };
    panel.addEventListener('touchend', end, { passive: true });
    panel.addEventListener('touchcancel', reset, { passive: true });
  }

  init() {
    this.setupDrawer();
    this.setupTriggerFloat();

    if (this.lists.length === 0) return;

    const contentSelector = this.lists[0].getAttribute('data-article-content');
    const contentEl = contentSelector ? document.querySelector(contentSelector) : null;
    if (!contentEl) return;

    this.headings = this.extractHeadings(contentEl);
    if (this.headings.length === 0) return;

    this.lists.forEach((listEl) => {
      this.buildIndexList(listEl);
      this.setupAnchors(listEl);
    });

    this.setupScrollSpy();

    if (this.headings.length > 0) {
      this.setActiveIndex(0);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ArticleIndex());
} else {
  new ArticleIndex();
}
